import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbDate, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { BrandService } from '../../shared/services/brand.service';

// App services
import { AnalyticsService } from '../../shared/services/analytics.service';
import { AuthService } from '../../shared/services/auth.service';
import { CryptoService } from '../../shared/services/crypto.service';
import { EmitterService } from '../../shared/services/emitter.service';
import { MtmBreadcrumbLink, MtmBreadcrumbService } from '../../shared/services/mtm-breadcrumb.service';
import { ProjectV2ServiceService } from '../../shared/services/project-v2-service.service';
import { ProjectService } from '../../shared/services/project.service';
import { ThemeService } from '../../shared/services/theme.service';
import { TimelineService } from '../../shared/services/timeline.service';
import { OverlayService } from '../../shared/services/overlayService';
import { FeatureConfigService } from '../../shared/services/feature-config.service';
import { GoogleAnalyticsService } from '../../shared/services/google-analytics.service';

// App components
import { ProjectsComponent } from '../projects/projects.component';

// App models and constants
import { CampaignMainFeatureConfig, FeatureTypes } from '../../shared/interfaces';
import { CAMPAIGN_STATUS_OPTIONS } from '../../shared/components/status-badge/status.const';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { CampaignSearchParams } from '../../shared/interfaces/workspace-search-params.interface';
import type { WorkspaceMode } from '../../shared/interfaces/workspace.interface';
import {
  CAMPAIGN_WORKSPACE_COMPONENT_NAMES,
  CAMPAIGN_WORKSPACE_CONSTANTS,
  CAMPAIGN_WORKSPACE_HEADER_LABELS,
  CAMPAIGN_WORKSPACE_ROUTES,
  CAMPAIGN_WORKSPACE_TRANSLATIONS,
} from '../../shared/models/campaign-workspace-translations';
import {
  BrandId,
  BrandUpdatedEvent,
  CalendarFilterUpdatedEvent,
  DriveCampaignEvent,
  ErrorValue,
} from '../../shared/models';
import { AppStateService, ActivePage } from '../../shared/services/app-state.service';

// Campaign drive services
import { CampaignDriveFilterService } from './services/campaign-drive-filter.service';
import { CampaignDriveWorkSpaceService } from './services/campaign-drive-workspace.service';
import { CampaignDriveService } from './services/campaign-drive.service';
import { CampaignDriveHelperService } from './services/campaign-drive-helper.service';
import { CampaignDriveConfigService } from './services/campaign-drive-config.service';
import { WorkspaceService } from '../../shared/services/workspace.service';
import { brandHasWorkspace } from '../../shared/utils/brand-workspace.utils';
import { showContentUnavailable } from '../../shared/utils/content-unavailable.utils';
import { PLATFORM_CHAT_ENTITY_TYPE } from '../../shared/components/platform-comments-chat/platform-chat-entity.const';
import { PlatformCommentsChatStateService } from '../../shared/services/platform-comments-chat-state.service';
import { PlatformTaskInterfaceStateService } from '../../shared/services/platform-task-interface-state.service';

@Component({
  selector: 'mtm-campaign-drive',
  templateUrl: './campaign-drive.component.html',
  styleUrls: ['./campaign-drive.component.scss', '../nav-tabs.scss'],
  standalone: false,
})
export class CampaignDriveComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChildren(NgbDropdown) settingsDropdown: QueryList<NgbDropdown>;
  @ViewChild('searchPanel', { static: false }) searchPanel: TemplateRef<any>;
  @ViewChild(NgbDropdown, { static: false }) filterMadDropdown: NgbDropdown;
  @ViewChild(ProjectsComponent) projects!: ProjectsComponent;

  private readonly ngUnsubscribe = new Subject();
  private readonly subscription: Subscription = new Subscription();
  private readonly searchDriveCampaignChanged: Subject<string> = new Subject<string>();
  private readonly getWorkspaceRequest$ = new Subject();
  protected campaignMainConfig: CampaignMainFeatureConfig | null;

  private readonly isLoadedSignal = signal<boolean>(false);
  private readonly isErrorSignal = signal<boolean>(false);
  private readonly driveCampaignsSignal = signal<any[]>([]);
  private readonly backupDriveCampaignsSignal = signal<any[]>([]);
  private readonly workspacesSignal = signal<any[]>([]);
  private readonly userBrandsSignal = signal<any[]>([]);
  private readonly currentBrandSignal = signal<any>(null);

  get isLoaded(): boolean {
    return this.isLoadedSignal();
  }
  set isLoaded(value: boolean) {
    this.isLoadedSignal.set(!!value);
  }

  get isError(): boolean {
    return this.isErrorSignal();
  }
  set isError(value: boolean) {
    this.isErrorSignal.set(!!value);
  }

  get DriveCampaigns(): any[] {
    return this.driveCampaignsSignal();
  }
  set DriveCampaigns(value: any[]) {
    this.driveCampaignsSignal.set(value ?? []);
  }

  get backupDriveCampaigns(): any[] {
    return this.backupDriveCampaignsSignal();
  }
  set backupDriveCampaigns(value: any[]) {
    this.backupDriveCampaignsSignal.set(value ?? []);
  }

  get workspaces(): any[] {
    return this.workspacesSignal();
  }
  set workspaces(value: any[]) {
    this.workspacesSignal.set(value ?? []);
  }

  get userBrands(): any[] {
    return this.userBrandsSignal();
  }
  set userBrands(value: any[]) {
    this.userBrandsSignal.set(value ?? []);
  }

  get currentBrand(): any {
    return this.currentBrandSignal();
  }
  set currentBrand(value: any) {
    this.currentBrandSignal.set(value ?? null);
  }

  inWorkspace: boolean = false;
  isActive: boolean = true;
  isInit: boolean = false;
  isWidPresent: boolean = false;
  firstInit: boolean = true;
  usersLoaded: boolean = false;
  hasNext: boolean = false;
  isLoadingMore: boolean = false;
  showGoBackToWorkspace: boolean = false;
  isCampaignCollapse: boolean = true;
  isProjectCollapse: boolean = true;
  favBackupDriveCampaigns = [];
  favDriveCampaigns = [];
  archiveDriveCampaigns = [];
  archiveBackupDriveCampaigns = [];
  timelines: any = [];
  usersList: any = {};
  productDevManagers = [];
  cncManagers = [];
  years = [];
  months = CAMPAIGN_WORKSPACE_CONSTANTS.CALENDAR_VIEW_MODES.MONTHS;
  categoryTags: string[] = [];

  searchDriveCampaign: string;
  cncQuery: string;
  productDevQuery: string;
  sortBy: string = CAMPAIGN_WORKSPACE_CONSTANTS.CALENDAR_VIEW_MODES.DATE;
  calendarViewMode = CAMPAIGN_WORKSPACE_CONSTANTS.CALENDAR_VIEW_MODES.LAST_UPDATED;
  campaignSearchParams: CampaignSearchParams;
  yearSelected: any[] = [];
  monthCollapse: any = {};
  madMonthYear: any = {};
  madLabel: string;
  calendarViewDates: any;

  activeTab: string = 'all';
  page: number = 1;
  pagingState: string;
  cacheBuster: string = new Date().getTime().toString();

  canCreateDriveCampaign: boolean = false;
  canCreateProject: boolean = false;
  canDeleteCampaign: boolean = false;
  canEditStatus: boolean = false;

  isStandAloneSearchEnabled: boolean = false;
  isCriteriaForm1Enabled: boolean;
  isCriteriaForm2Enabled: boolean;
  isCriteriaFormTimelineSimplified1Enabled: boolean;
  private readonly workspaceTimelineConfig = toSignal(
    this.route.queryParams.pipe(
      map((query) => (query['wid'] ? this.cryptoService.decryptUsingAES256(query['wid']) : null)),
      distinctUntilChanged(),
      switchMap((id) => this.featureConfigService.getWorkspaceFeatureConfigs([FeatureTypes.FEA_CAMPAIGN_TIMELINE], id)),
      map((configs) => configs[FeatureTypes.FEA_CAMPAIGN_TIMELINE])
    )
  );
  get hasSimulator(): boolean {
    const config = this.workspaceTimelineConfig();
    return !!config?.active && !!config.hasSimulator;
  }
  hasCncManager: boolean;
  hasProductDevManager: boolean;
  hasActiveProduct: boolean;
  isWorkspaceEnabled: boolean;

  allProductDevManagerSelected: boolean = false;
  allCnCManagerSelected: boolean = false;
  allMadSelected: boolean = false;

  isAll: boolean = true;
  isFav: boolean = false;
  isFavorites: boolean = false;
  isArchived: boolean = false;
  isAllProject: boolean = true;
  isFavoritesProject: boolean = false;
  isArchivedProject: boolean = false;

  authUser: any;
  username: any;
  initialName: any;
  nameColor: any;
  blobPicture: any;

  workspaceId: string;
  workspaceName: string;
  workspaceMode: WorkspaceMode | null = null;
  brandId: string;
  banner: any;
  allBrandParticipants: any[] = [];
  activePage: string = ActivePage.CampaignOverview;

  selectedWorkspace: any = {
    name: CAMPAIGN_WORKSPACE_TRANSLATIONS.ALL_WORKSPACES,
    id: null,
  };

  readonly campaignStatusOptions = CAMPAIGN_STATUS_OPTIONS;
  headerLabel = CAMPAIGN_WORKSPACE_HEADER_LABELS.CAMPAIGNS;
  linksLabel: string;
  newCampaignLabel: string;
  platformChatEntityType: string = PLATFORM_CHAT_ENTITY_TYPE.WORKSPACE;
  isAgencyUser = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly projectV2Service: ProjectV2ServiceService,
    private readonly translatePipe: TranslatePipe,
    private readonly analyticService: AnalyticsService,
    private readonly authService: AuthService,
    private readonly brandService: BrandService,
    private readonly cryptoService: CryptoService,
    private readonly featureConfigService: FeatureConfigService,
    private readonly workspaceService: WorkspaceService,
    public readonly timelineService: TimelineService,
    private readonly projectService: ProjectService,
    private readonly mtmBreadcrumbService: MtmBreadcrumbService,
    private readonly overlayService: OverlayService,
    private readonly themeService: ThemeService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly campaignDriveFilterService: CampaignDriveFilterService,
    private readonly campaignDriveWorkSpaceService: CampaignDriveWorkSpaceService,
    private readonly campaignDriveService: CampaignDriveService,
    private readonly campaignDriveHelperService: CampaignDriveHelperService,
    private readonly campaignDriveConfigService: CampaignDriveConfigService,
    private readonly appState: AppStateService,
    private readonly platformChatState: PlatformCommentsChatStateService,
    private readonly platformTaskState: PlatformTaskInterfaceStateService
  ) {
    this.initializeComponent();
  }

  get isCommentPageCommentExpanded(): boolean {
    return (
      this.appState.isEnableCommentPage() && this.appState.isCommentPageCommentExpanded(ActivePage.CampaignOverview)
    );
  }

  get canAccessCampaign(): boolean {
    return this.campaignMainConfig?.active && !this.isAgencyUser;
  }

  // INITIALIZATION METHODS
  private initializeComponent(): void {
    this.mtmBreadcrumbService.setLinks([]);
    this.setBreadcrumb();
    this.timelineService.lastSelectedDriveCampaign = null;
    this.timelineService.isDriveNav.emit(true);

    this.setupUserAvatarEvents();
    this.setupBrandEvents();
    this.setupSearchEvents();

    this.getDriveCampaignSearchParams();
  }

  private setupUserAvatarEvents(): void {
    EmitterService.get('authUser.changeAvatar')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data) => {
        if (data?.externalUser) {
          this.username = data.user.email;
          setTimeout(() => {
            this.reloadProfilePicture();
          }, 350);
        } else if (data && data.email === this.username) {
          setTimeout(() => {
            this.reloadProfilePicture();
          }, 350);
        }
      });
  }

  private setupBrandEvents(): void {
    EmitterService.get(BrandUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.setBreadcrumb();
          this.setCurrentSelectedBrand();
        },
      });
  }

  private setupSearchEvents(): void {
    this.searchDriveCampaignChanged.pipe(debounceTime(350), distinctUntilChanged()).subscribe((model) => {
      this.searchDriveCampaign = model;
      this.searchDriveCampaigns();
    });
  }

  // LIFECYCLE HOOKS
  ngOnInit(): void {
    this.initializeBasicState();
    this.handleQueryParams();
    this.loadInitialData();
    this.setUserPermissions();
    this.checkWidPresence();
    this.loadFeatureConfigs();
    this.buildAllBrandParticipants();
    this.loadCampaignFeatureConfig();
  }

  ngAfterViewInit(): void {
    this.setBreadcrumb();
    this.campaignDriveHelperService.setupContentAndBreadcrumbs(
      this.searchPanel,
      this.isStandAloneSearchEnabled,
      this.workspaceId
    );
    this.updatePlatformChatContext();
  }

  private updatePlatformChatContext(): void {
    if (this.workspaceId && this.currentBrand?.id) {
      this.platformChatState.setContext({
        entityType: this.platformChatEntityType,
        pageId: this.workspaceId,
        entityName: this.workspaceName,
        participants: this.allBrandParticipants,
        // Drive-mode workspaces use the drive feed (WORKSPACE directory uuid ==
        // workspace id); legacy workspaces keep the old chat.
        driveAnchorUuid: this.workspaceMode === 'drive' ? this.workspaceId : null,
      });
      this.updatePlatformTaskContext();
    }
  }

  private updatePlatformTaskContext(): void {
    if (this.workspaceId && this.workspaceName) {
      this.platformTaskState.setContext({
        entityType: this.platformChatEntityType,
        pageId: this.workspaceId,
        entityName: this.workspaceName,
      });
    }
  }

  private buildAllBrandParticipants(): void {
    const brand = this.authService.getUserBrand();
    if (!brand?.id) {
      return;
    }

    // Brand payloads no longer carry usernamesRoles — the chat participant list
    // comes from the brand-users endpoint instead.
    this.brandService
      .getBrandUsers(brand.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((users) => {
        const emailsSet = new Set<string>();
        const participants: { email: string }[] = [];
        (users ?? []).forEach((user) => {
          const email = user.email || user.username || '';
          const normalized = email.toLowerCase();
          if (normalized && !emailsSet.has(normalized)) {
            emailsSet.add(normalized);
            participants.push({ email });
          }
        });
        this.allBrandParticipants = participants;
      });
  }

  ngOnDestroy(): void {
    this.mtmBreadcrumbService.setLinks([]);
    this.timelineService.workspaceName = this.workspaceName;
    this.themeService.updateContent({
      searchPanel: null,
      standaloneSearch: null,
      fullWidthContent: false,
    });

    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
    this.subscription.unsubscribe();
  }

  // INITIALIZATION HELPERS
  private initializeBasicState(): void {
    const activeWs = this.workspaceService.getActiveWorkspace();
    if (activeWs) {
      this.workspaceService.unsetActiveWorkspace();
    }

    this.setCurrentSelectedBrand();
    this.campaignDriveWorkSpaceService.isInsideWorkspace().subscribe((value) => {
      this.inWorkspace = value;
    });

    this.timelineService.driveCampaignData = null;
    this.timelineService.activeCampaign$.next(null);
    this.authUser = this.authService.getAuthUser();
    this.isAgencyUser = this.authUser?.userPermissionType === 'AGENCY';
    this.workspaceName = this.timelineService.workspaceName;
    this.brandId = localStorage.getItem(BrandId);
    this.nameColor = this.campaignDriveHelperService.stringToHslColor(this.username);
  }

  private loadCampaignFeatureConfig(): void {
    this.featureConfigService
      .getFeatureConfigs([FeatureTypes.FEA_WORKSPACE_MAIN, FeatureTypes.FEA_CAMPAIGN_MAIN])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.campaignMainConfig = response[FeatureTypes.FEA_CAMPAIGN_MAIN];
        },
      });
  }

  private handleQueryParams(): void {
    // Only a workspace change matters: `?ctx=` writes (the comment feed's state) also
    // re-emit queryParams and must not refetch the workspace.
    this.route.queryParams
      .pipe(
        distinctUntilChanged((a, b) => a['wid'] === b['wid']),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((query) => {
        this.showGoBackToWorkspace = !!query.wid;

        if (!query.wid) {
          // The component is reused when navigating from ?wid=X to no wid — drop the previous workspace.
          this.workspaceId = '';
          this.workspaceName = '';
          this.workspaceMode = null;
          this.setBreadcrumb();
          this.getDriveCampaignSearchParams();
          this.timelineService.workspaceId = '';
          this.appState.setActivePage(null);
          this.platformChatState.setContext(null);
          this.platformTaskState.setContext(null);
          return;
        }

        const brand = this.authService.getUserBrand();
        this.workspaceId = this.cryptoService.decryptUsingAES256(query.wid);

        this.timelineService.workspaceId = this.workspaceId;
        this.timelineService.workspaceName = '';
        this.workspaceName = '';
        this.getDriveCampaignSearchParams();
        this.campaignDriveWorkSpaceService.getWorkspaceData(this.authUser.companyId, this.workspaceId).subscribe({
          next: (ws) => {
            if (ws?.name) {
              this.workspaceName = ws.name;
              this.workspaceMode = ws.mode ?? null;
              this.updatePlatformChatContext();
              this.updatePlatformTaskContext();
              this.setBreadcrumb();
            }
          },
          error: () => showContentUnavailable(this.router),
        });

        if (this.workspaceId) {
          this.setBreadcrumbs(brand);
        }
        this.getDriveCampaignSearchParams();
        if (this.workspaceId) {
          this.appState.setActivePage(ActivePage.CampaignOverview);
          this.appState.ensureCommentPageExpandedOnFirstLoad(ActivePage.CampaignOverview);
          this.updatePlatformChatContext();
        } else {
          this.appState.setActivePage(null);
        }
      });
  }

  private loadInitialData(): void {
    this.getWorkspaceDriveCampaign();
    this.setProfilePicture(this.authUser);
    this.getSideNavWorkspace();
    this.getWorkspaces();
    this.isInit = true;
  }

  private setUserPermissions(): void {
    this.canEditStatus = this.campaignDriveConfigService.canEditStatus();
    this.canCreateDriveCampaign = this.campaignDriveConfigService.canCreateCampaign(this.authUser.userPermissionType);
    this.canCreateProject = this.campaignDriveConfigService.canCreateProject(this.authUser.userPermissionType);
    this.canDeleteCampaign = this.campaignDriveConfigService.canDeleteCampaign();
  }

  private checkWidPresence(): void {
    this.route.queryParams.subscribe((params) => {
      const wid = params['wid'];
      this.isWidPresent = !!wid;
    });
  }

  private loadFeatureConfigs(): void {
    this.featureConfigService
      .getFeatureConfigs([
        FeatureTypes.FEA_CAMPAIGN_MAIN,
        FeatureTypes.FEA_CAMPAIGN_TIMELINE,
        FeatureTypes.FEA_WORKSPACE_MAIN,
      ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.handleFeatureConfigResponse(response);
        },
      });
  }

  // DATA FETCHING METHODS
  getWorkspaceDriveCampaign(append: boolean = false) {
    if (!append) {
      this.isLoaded = false;
      this.isError = false;
      this.DriveCampaigns = [];
      this.hasNext = false;
      this.pagingState = '';
    }
    this.getWorkspaceRequest$.next(true);

    this.campaignDriveService
      .getWorkspaceDriveCampaign(
        this.workspaceId,
        this.isAll,
        this.isArchived,
        this.campaignSearchParams,
        this.calendarViewDates,
        this.pagingState,
        this.firstInit,
        this.cacheBuster,
        this.months,
        CAMPAIGN_WORKSPACE_TRANSLATIONS
      )
      .pipe(takeUntil(this.getWorkspaceRequest$))
      .subscribe({
        next: (result) => {
          const processed = this.campaignDriveService.processWorkspaceResult(
            result,
            this.firstInit,
            this.cacheBuster,
            this.months,
            CAMPAIGN_WORKSPACE_TRANSLATIONS,
            this.isArchived,
            this.years,
            this.madMonthYear,
            this.categoryTags
          );

          this.hasNext = processed.hasNext;
          this.pagingState = processed.pagingState;
          if (append) {
            this.DriveCampaigns = this.campaignDriveService.mergeCampaignPages(
              this.DriveCampaigns,
              processed.campaigns
            );
            this.backupDriveCampaigns = this.campaignDriveService.mergeCampaignPages(
              this.backupDriveCampaigns,
              processed.backup
            );
            this.favDriveCampaigns = this.DriveCampaigns;
            this.favBackupDriveCampaigns = this.backupDriveCampaigns;
            this.archiveDriveCampaigns = this.isArchived ? this.DriveCampaigns : this.archiveDriveCampaigns;
            this.archiveBackupDriveCampaigns = this.isArchived
              ? this.backupDriveCampaigns
              : this.archiveBackupDriveCampaigns;
            this.isLoadingMore = false;
          } else {
            this.DriveCampaigns = processed.campaigns;
            this.backupDriveCampaigns = processed.backup;
            this.favDriveCampaigns = processed.favDriveCampaigns;
            this.favBackupDriveCampaigns = processed.favBackupDriveCampaigns;
            this.archiveDriveCampaigns = processed.archiveDriveCampaigns;
            this.archiveBackupDriveCampaigns = processed.archiveBackupDriveCampaigns;
          }

          this.setCampaignPermissions();
          this.isInit = false;
          this.getFavoriteDriveCampaigns(true);

          if (this.firstInit) {
            this.setYearSelected();
            this.generateMadLabel();
            this.firstInit = false;
          }
        },
        error: (err) => {
          console.error('error', err);
          this.isLoadingMore = false;
          if (!append) {
            this.isError = true;
            this.isLoaded = false;
          }
        },
      });
  }

  getUsers() {
    this.campaignDriveHelperService
      .getUsers(this.workspaceId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          this.productDevManagers = res.productDevManagers;
          this.cncManagers = res.cncManagers;
        },
      });
  }

  getWorkspaces() {
    this.campaignDriveWorkSpaceService.getWorkspaces(this.workspaceId, this.isStandAloneSearchEnabled).subscribe({
      next: ({ workspaces, userBrands }) => {
        this.workspaces = workspaces;
        this.userBrands = userBrands;
        this.getUsers();
      },
      error: (err) => console.error('Error fetching workspaces:', err),
    });
  }

  getFavoriteDriveCampaigns(preserveDisplay = false) {
    if (!preserveDisplay) {
      this.isError = false;
      this.isLoaded = false;
      this.DriveCampaigns = [];
      this.backupDriveCampaigns = [];
    }

    this.campaignDriveService
      .getFavoriteItems(DriveCampaignEvent, this.campaignSearchParams)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: any[]) => {
          if (this.isFav) {
            const { drive, backup } = this.campaignDriveService.processFavMode(res, {
              workspaceId: this.workspaceId ?? null,
              cacheBuster: this.cacheBuster,
              defaultActiveProductLabel: CAMPAIGN_WORKSPACE_TRANSLATIONS.YES,
            });
            this.DriveCampaigns = drive;
            this.backupDriveCampaigns = backup;
          } else if (this.isAll) {
            const { drive, backup } = this.campaignDriveService.processAllMode(res, {
              favDriveCampaigns: this.favDriveCampaigns,
              favBackupDriveCampaigns: this.favBackupDriveCampaigns,
            });
            this.DriveCampaigns = drive;
            this.backupDriveCampaigns = backup;
          } else if (this.isArchived) {
            const { drive, backup } = this.campaignDriveService.processArchivedMode(res, {
              archiveDriveCampaigns: this.archiveDriveCampaigns,
              archiveBackupDriveCampaigns: this.archiveBackupDriveCampaigns,
            });
            this.DriveCampaigns = drive;
            this.backupDriveCampaigns = backup;
          }

          this.isLoaded = true;

          if (this.searchDriveCampaign) {
            this.searchDriveCampaigns();
          }
          if (this.sortBy) {
            this.sortCampaign(this.sortBy);
          }
          this.filterCampaignByBrandWorkspace();
        },
        error: (err) => {
          console.error(err);
          this.isError = true;
          this.isLoaded = true;
        },
      });
  }

  getSideNavWorkspace() {
    this.timelineService.selectedWorkspace.subscribe((wk: any) => {
      if (wk?.id) {
        this.timelineService.componentName.next({
          componentName: CAMPAIGN_WORKSPACE_COMPONENT_NAMES.CAMPAIGN_DRIVE_COMPONENT,
          bannerUrl: wk.coverUrl,
        });
        this.workspaceId = wk.id;
        this.timelineService.workspaceId = this.workspaceId;
        if (wk.name) {
          this.timelineService.workspaceName = wk.name;
          this.workspaceName = wk.name;
        }
        this.updatePlatformChatContext();
        this.getWorkspaceDriveCampaign();
      }
    });
  }

  // UI INTERACTION METHODS
  searchQueryChanged(query: string) {
    this.searchDriveCampaignChanged.next(query);
  }

  searchDriveCampaigns() {
    if (!this.isLoaded) {
      return;
    }

    this.isLoaded = false;
    setTimeout(() => {
      this.DriveCampaigns = this.campaignDriveService.searchDriveCampaigns(
        this.searchDriveCampaign,
        this.backupDriveCampaigns
      );

      this.filterCampaignByBrandWorkspace();
      this.isLoaded = true;
    }, 100);
  }

  createCampaignDrive(e: any) {
    this.projectV2Service.driveCoordinatorMilestone = undefined;
    this.projectV2Service.timelineId = null;
    this.projectV2Service.updateFormData({}, {});
    const url = this.workspaceId
      ? `workspaces/campaigndrive/${this.workspaceId}/general`
      : 'workspaces/campaigndrive/general';
    this.router
      .navigate([url])
      .then(() => {})
      .catch((err) => console.error('Navigation error:', err));
    this.timelineService.isDriveNav.emit(false);
  }

  redirectToDashboard(drive: any) {
    this.campaignDriveHelperService.redirectToDashboard(drive);
  }

  gotoWorkspace(project: any) {
    this.campaignDriveHelperService.gotoWorkspace(project, this.isWorkspaceEnabled);
  }

  editCampaignDrive(drive: any) {
    this.projectV2Service
      .updateSelectedDriveCampaign(drive.workspaceId, drive.id)
      .then(() => {})
      .catch((err) => console.error('Navigation error:', err));
  }

  changeDriveCampaignStatus(item: any, status: any) {
    item.status = status;
    this.projectV2Service.updateDriveCampaignStatus(item.workspaceId, item.id, status).subscribe(() => {});
  }

  changeDriveCampaignActiveProduct(item: any, activeProduct: any) {
    item.activeProduct = activeProduct;
    this.projectV2Service.updateDriveCampaign(item).subscribe(() => {});
  }

  deleteDriveCampaign(item: any) {
    this.campaignDriveService.deleteDriveCampaign(item).subscribe({
      next: (result: boolean | undefined) => {
        if (result) {
          this.DriveCampaigns = this.DriveCampaigns.filter((d: any) => d.id !== item.id);
        }
      },
      error: (err) => {
        console.error('Delete failed', err);
      },
    });
  }

  markAsFavorite(driveCampaign: any) {
    this.analyticService
      .markAsFavorite(driveCampaign, DriveCampaignEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: any) => {
          this.DriveCampaigns = this.DriveCampaigns.map((dc) => ({
            ...dc,
            isFavorite: res.items.includes(dc.id),
          }));
        },
        error: (err) => {
          driveCampaign.isFavorite = false;
          console.error(err);
        },
      });
  }

  removeFavorite(driveCampaign: any) {
    this.analyticService
      .removeAsFavorite(driveCampaign, DriveCampaignEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.getWorkspaceDriveCampaign();
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  archiveDriveCampaign(item: any) {
    this.campaignDriveService
      .archiveDriveCampaign(item, this.DriveCampaigns)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (updatedList) => {
          this.DriveCampaigns = updatedList;
        },
        error: (err) => {
          this.overlayService.showError(this.translatePipe.transform(err.errorCode, ErrorValue));
        },
      });
  }

  createProject() {
    const queryParams = this.workspaceId ? { workspaceId: this.workspaceId } : {};
    this.router
      .navigate([CAMPAIGN_WORKSPACE_ROUTES.NEW_COMPANY_PROJECTS], { queryParams })
      .then(() => {})
      .catch((err) => console.error('Navigation error:', err));
  }

  goToSimulator() {
    this.router
      .navigate([CAMPAIGN_WORKSPACE_ROUTES.TIMELINE_SIMULATOR])
      .then(() => {})
      .catch((err) => console.error('Navigation error:', err));
  }

  // FILTER/SORT METHODS
  selectCalendarMode(mode: string) {
    const { dates, viewMode } = this.campaignDriveService.selectCalendarMode(mode);

    this.calendarViewDates = dates;
    this.calendarViewMode = viewMode;

    EmitterService.get(CalendarFilterUpdatedEvent).emit(this.calendarViewDates);
    this.getWorkspaceDriveCampaign();
  }

  sortCampaign(mode: string) {
    this.sortBy = mode;
    this.DriveCampaigns = this.campaignDriveService.sortCampaign(this.DriveCampaigns, mode);
    this.setDriveCampaignSearchParams();
  }

  selectProductDevOwner(username: string) {
    const updated = this.campaignDriveFilterService.toggleProductDevOwner(
      username,
      this.campaignSearchParams,
      this.productDevManagers
    );

    this.campaignSearchParams = updated.campaignSearchParams;
    this.allProductDevManagerSelected = updated.allProductDevManagerSelected;

    this.getWorkspaceDriveCampaign();
  }

  selectCnCManager(username: string) {
    const updated = this.campaignDriveFilterService.toggleCnCManager(
      username,
      this.campaignSearchParams,
      this.cncManagers
    );

    this.campaignSearchParams = updated.campaignSearchParams;
    this.allCnCManagerSelected = updated.allCnCManagerSelected;

    this.getWorkspaceDriveCampaign();
  }

  setActiveProduct(value: string) {
    this.campaignSearchParams = this.campaignDriveFilterService.toggleActiveProduct(value, this.campaignSearchParams);

    this.getWorkspaceDriveCampaign();
  }

  selectAllProductDevManager(e: any) {
    const updated = this.campaignDriveFilterService.selectAllProductDevManager(
      e,
      this.allProductDevManagerSelected,
      this.campaignSearchParams,
      this.productDevManagers
    );

    this.allProductDevManagerSelected = updated.allProductDevManagerSelected;
    this.campaignSearchParams = updated.campaignSearchParams;

    this.getWorkspaceDriveCampaign();
  }

  selectAllCnCManager(e: any) {
    const updated = this.campaignDriveFilterService.selectAllCnCManager(
      e,
      this.allCnCManagerSelected,
      this.campaignSearchParams,
      this.cncManagers
    );

    this.allCnCManagerSelected = updated.allCnCManagerSelected;
    this.campaignSearchParams = updated.campaignSearchParams;

    this.getWorkspaceDriveCampaign();
  }

  selectAllMad(e: any) {
    const updated = this.campaignDriveFilterService.selectAllMad(
      e,
      this.allMadSelected,
      this.campaignSearchParams,
      this.years,
      this.months,
      this.madMonthYear,
      this.yearSelected
    );

    this.allMadSelected = updated.allMadSelected;
    this.campaignSearchParams = updated.campaignSearchParams;
    this.madMonthYear = updated.madMonthYear;
    this.yearSelected = updated.yearSelected;

    this.getWorkspaceDriveCampaign();
    this.generateMadLabel();
  }

  selectYear(e: any, year: number) {
    const updated = this.campaignDriveFilterService.selectYear(
      e,
      year,
      this.months,
      this.campaignSearchParams,
      this.madMonthYear,
      this.yearSelected
    );

    this.campaignSearchParams = updated.campaignSearchParams;
    this.madMonthYear = updated.madMonthYear;
    this.yearSelected = updated.yearSelected;

    this.getWorkspaceDriveCampaign();
    this.generateMadLabel();
  }

  monthSelected(e: any, year: number, month: string) {
    const updated = this.campaignDriveFilterService.monthSelected(
      e,
      year,
      month,
      this.months,
      this.campaignSearchParams,
      this.madMonthYear,
      this.yearSelected
    );

    this.campaignSearchParams = updated.campaignSearchParams;
    this.madMonthYear = updated.madMonthYear;
    this.yearSelected = updated.yearSelected;

    this.getWorkspaceDriveCampaign();
    this.generateMadLabel();
  }

  // VIEW TOGGLE METHODS
  allItems() {
    this.isArchived = false;
    this.isFavorites = false;
    this.isFav = false;
    this.isAll = true;
    this.getWorkspaceDriveCampaign();
  }

  favoriteItems() {
    this.isArchived = false;
    this.isFavorites = true;
    this.isFav = true;
    this.isAll = false;
    this.getFavoriteDriveCampaigns();
  }

  getArchiveCampaign() {
    this.isArchived = true;
    this.isFavorites = false;
    this.isFav = false;
    this.isAll = false;
    this.getWorkspaceDriveCampaign();
  }

  allProjectItems() {
    this.isArchivedProject = false;
    this.isFavoritesProject = false;
    this.isAllProject = true;
    this.timelineService.emitIsAll(true);
  }

  favoriteProjectItems() {
    this.isArchivedProject = false;
    this.isFavoritesProject = true;
    this.isAllProject = false;
    this.timelineService.emitIsFavorites(true);
  }

  getArchiveProjects() {
    this.isArchivedProject = true;
    this.isFavoritesProject = false;
    this.isAllProject = false;
    this.timelineService.emitIsArchived(true);
  }

  // UTILITY METHODS
  parseTimestamp(timestamp: number): NgbDate {
    const date = new Date(timestamp);
    return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  }

  setCurrentSelectedBrand() {
    this.currentBrand = this.authService.getUserBrand();
  }

  setDriveCampaignSearchParams() {
    this.campaignDriveHelperService.setDriveCampaignSearchParams(
      this.workspaceId,
      this.campaignSearchParams,
      this.sortBy
    );
  }

  getDriveCampaignSearchParams() {
    const params = this.campaignDriveHelperService.getDriveCampaignSearchParams(this.workspaceId);
    this.campaignSearchParams = params.campaignSearchParams;
    this.sortBy = params.sortBy;
  }

  setYearSelected() {
    this.yearSelected = this.campaignDriveHelperService.setYearSelected(
      this.campaignSearchParams,
      this.madMonthYear,
      this.years
    );
  }

  setCampaignPermissions() {
    this.DriveCampaigns = this.campaignDriveHelperService.setCampaignPermissions(this.DriveCampaigns, this.authUser);
  }

  filterCampaignByBrandWorkspace() {
    const brand = this.authService.getUserBrand();
    if (!brand) return;
    this.DriveCampaigns = this.DriveCampaigns.filter((w) => {
      return brandHasWorkspace(brand, w.workspaceId);
    });
  }

  generateMadLabel() {
    this.campaignDriveFilterService.madMonthYear = this.madMonthYear;
    this.madLabel = this.campaignDriveFilterService.generateMadLabel(this.years, this.months, this.translatePipe);
  }

  handleItemsScroll() {
    if (!this.settingsDropdown) return;
    this.settingsDropdown.forEach((dropdown: NgbDropdown) => {
      if (dropdown.isOpen()) {
        dropdown.close();
      }
    });
  }

  loadMore(check: boolean) {
    if (!check || !this.hasNext || !this.pagingState || this.isLoadingMore) return;
    this.isLoadingMore = true;
    this.getWorkspaceDriveCampaign(true);
  }

  // USER PROFILE METHODS
  private setProfilePicture(currentUser: any) {
    const result = this.campaignDriveHelperService.setProfilePicture(currentUser);

    if (result) {
      this.initialName = result.initialName;
      this.nameColor = result.nameColor;
      this.blobPicture = result.blobPicture;
    }
  }

  reloadProfilePicture() {
    this.usersLoaded = false;
    this.usersList[this.username] = void 0;
  }

  // BREADCRUMB METHODS
  setBreadcrumb() {
    const brand = this.authService.getUserBrand();

    if (this.isWidPresent && this.workspaceId) {
      const pageLinks: MtmBreadcrumbLink[] = [
        {
          label: brand?.name,
          url: ['/'],
        },
        {
          label: CAMPAIGN_WORKSPACE_COMPONENT_NAMES.WORKSPACE,
          url: [CAMPAIGN_WORKSPACE_ROUTES.WORKSPACES],
        },
        {
          label: this.workspaceName,
          url: [CAMPAIGN_WORKSPACE_ROUTES.WORKSPACES, CAMPAIGN_WORKSPACE_COMPONENT_NAMES.CAMPAIGN_DRIVE],
          queryParams: { wid: this.cryptoService.encryptString(this.workspaceId) },
        },
      ];
      this.mtmBreadcrumbService.setLinks(pageLinks);
    } else {
      const pageLinks: MtmBreadcrumbLink[] = [
        {
          label: brand?.name,
          url: ['/'],
        },
        {
          label: this.linksLabel,
        },
      ];
      this.mtmBreadcrumbService.setLinks(pageLinks);
    }
    this.googleAnalyticsService.sendUserAnalyticsConfig('', '', '');
  }

  private setBreadcrumbs(brand: any): void {
    const pageLinks: MtmBreadcrumbLink[] = [
      { label: brand.name },
      {
        label: this.workspaceName || this.selectedWorkspace.name,
        url: [CAMPAIGN_WORKSPACE_ROUTES.WORKSPACES, CAMPAIGN_WORKSPACE_COMPONENT_NAMES.CAMPAIGN_DRIVE],
        queryParams: { wid: this.cryptoService.encryptString(this.workspaceId) },
      },
      { label: CAMPAIGN_WORKSPACE_COMPONENT_NAMES.CAMPAIGN },
    ];
    this.mtmBreadcrumbService.setLinks(pageLinks);
  }

  // FEATURE CONFIG HANDLING
  private handleFeatureConfigResponse(response: any): void {
    const config = this.campaignDriveConfigService.parseCampaignFeatureConfig(response);

    this.headerLabel = config.headerLabel;
    this.linksLabel = config.linksLabel;
    this.newCampaignLabel = config.newCampaignLabel;

    this.isStandAloneSearchEnabled = config.isStandAloneSearchEnabled;
    this.isCriteriaForm1Enabled = config.isCriteriaForm1Enabled;
    this.isCriteriaForm2Enabled = config.isCriteriaForm2Enabled;
    this.isCriteriaFormTimelineSimplified1Enabled = config.isCriteriaFormTimelineSimplified1Enabled;

    this.hasCncManager = config.hasCncManager;
    this.hasProductDevManager = config.hasProductDevManager;
    this.hasActiveProduct = config.hasActiveProduct;
    this.isWorkspaceEnabled = config.isWorkspaceEnabled;
  }
}
